import React, { useState, useEffect } from 'react';
import { AuxTypeCode } from '@/types/device';
import { ScheduleInfo } from '@/types/dashboard';
import { FiUser, FiClock, FiSettings, FiActivity, FiInfo, FiDroplet, FiPlay, FiPause, FiSquare, FiAlertTriangle, FiSlash, FiRefreshCw } from 'react-icons/fi';
import { BsCloudRain } from 'react-icons/bs';
import { SchedulePanel } from "@/components/ui/DeviceCard/schedulePanel";
import { formatMinutesToTime } from '@/utils/time';
import { Aux, Dashboard, Pump } from '@/types/dashboard';
import { getThreeSpeedIndex, getThreeSpeedManualLabel, getThreeSpeedShortLabel, getThreeSpeedSpeedByIndex, getWeekdayArray, isMotorRunning, isThreeSpeedPump, isVSDPump, MotorStatus, MOTOR_STATUS_DISPLAY_NAMES, snapThreeSpeedSpeed, THREE_SPEED_LOW } from '@/utils/utils';
import { getHighestPriorityPumpMode } from '@/utils/pumpMode';
import { Colors } from '@/constants/colors';
import { getNextActionText } from '@/utils/schedule';
import { formatAuxTimerCountdown, formatAuxTimerDuration } from '@/utils/auxTimer';
import LinearProgress from '@mui/material/LinearProgress';
import Switch from '@mui/material/Switch';
import Slider from '@mui/material/Slider';
import SpaCoverIcon from '@/assets/svg/SpaCoverIcon';
import HeaterCoverIcon from '@/assets/svg/HeaterCoverIcon';

// Icons moved to '@/assets/svg' and imported above


interface MainPumpSectionProps {
  pump: Pump;
  scheduleInfo: ScheduleInfo | undefined;
  pumpLinkedDeviceList: Aux[];
  dashboard: Dashboard;
  pumpAux: Aux;
  setSpeedUI: (speed: number) => void;
  onPumpToggle: (isOn: boolean) => void;
  onPumpSpeedChange: (speed: number) => void;
  onPumpLinkedDeviceToggle: (typeCode: number, slotNum: number, IsOn: boolean) => void;
  isSpaOn: boolean;
  expectMotoStatus?: number | null;
}

const DRY_RUN_PROTECTION_TOTAL_SECONDS = 600;

const DRY_RUN_STATUS_LABELS: Record<number, string> = {
  0: 'Idle',
  1: 'Over Limit',
  2: 'Pump Retry',
  3: 'Protection Off',
  4: 'Priming',
};

const getDryRunStatusClassName = (status: number): string => {
  switch (status) {
    case 1:
      return 'bg-red-100 text-red-700';
    case 2:
      return 'bg-pink-100 text-pink-700';
    case 3:
      return 'bg-slate-200 text-slate-700';
    case 4:
      return 'bg-cyan-100 text-cyan-700';
    default:
      return 'bg-slate-100 text-slate-700';
  }
};

const formatCountdown = (seconds: number): string => {
  const safeSeconds = Math.max(0, seconds);
  const minutes = Math.floor(safeSeconds / 60);
  const remain = safeSeconds % 60;
  return `${minutes}:${remain.toString().padStart(2, '0')}`;
};

const MainPumpSection: React.FC<MainPumpSectionProps> = ({
  pump,
  scheduleInfo,
  pumpLinkedDeviceList,
  dashboard,
  pumpAux,
  setSpeedUI,
  onPumpToggle,
  onPumpSpeedChange,
  onPumpLinkedDeviceToggle,
  isSpaOn,
  expectMotoStatus
}) => {
  const [isVSD, setIsVSD] = useState<boolean>(false);

  useEffect(() => {
    setIsVSD(isVSDPump(pump.pump_type));
  }, [pump.pump_type]);
  const isThreeSpeed = isThreeSpeedPump(pump.pump_brand, pump.pump_model);

  const nextAction = scheduleInfo ? getNextActionText(scheduleInfo, AuxTypeCode.SPALSHME_PUMP) : "";
  const isCooldown = Boolean(pump?.pump_cooldown);
  const pumpIsOn = expectMotoStatus
    ? expectMotoStatus === MotorStatus.RUNNING
    : Boolean(isMotorRunning(pump.motor_status as MotorStatus));
  const hasPumpSchedule = Boolean(
    (scheduleInfo?.schedules || []).some(
      (s) =>
        s.aux_type_code === AuxTypeCode.SPALSHME_PUMP &&
        ((s.aux_slot ?? s.slot_num) === (pumpAux?.aux_slot_num ?? 0))
    )
  );
  const highestPriorityMode = getHighestPriorityPumpMode(pump.speed_priority);
  const hasPumpTimerConfigured = (pumpAux?.aux_timer ?? 0) > 0;
  const showPumpTimerCountdown =
    hasPumpTimerConfigured && !!pumpAux?.aux_status && (pumpAux?.aux_timer_remain_sec ?? 0) > 0;
  const dryRunInfo = pump?.dry_run_info;
  const hasDryRunStatus = Boolean(pump?.dry_run_supported && dryRunInfo?.activated);
  const isDryRunPriming = Boolean(hasDryRunStatus && dryRunInfo && dryRunInfo.current_round <= 1);
  const dryRunProgress = dryRunInfo
    ? Math.min(
        100,
        Math.max(
          0,
          (dryRunInfo.protection_remaining_second / DRY_RUN_PROTECTION_TOTAL_SECONDS) * 100
        )
      )
    : 0;

  return (
    <div>
      <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-700 rounded-lg p-6 shadow-lg">
        {/* Main Pump Card */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse"></div>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-slate-100">Main Pump</h3>
                <p className="text-base font-medium text-emerald-500">
                  {pump.pump_type}
                </p>
                {(pump.pump_brand || pump.pump_model) && (
                  <div>
                    <p className="text-sm font-medium text-emerald-500">
                      {[pump.pump_brand, pump.pump_model]
                        .filter(Boolean)
                        .join(" - ")}
                    </p>
                  </div>
                )}
                {nextAction && (
                  <div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">{nextAction}</p>
                  </div>
                )}
                {hasPumpTimerConfigured && (
                  <div className="mt-2 flex flex-col gap-1">
                    <span className="inline-flex w-fit items-center rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 dark:border-slate-700 dark:text-slate-300">
                      <FiClock className="mr-1.5 h-3.5 w-3.5" />
                      Timer {formatAuxTimerDuration(pumpAux.aux_timer ?? 0)}
                    </span>
                    {showPumpTimerCountdown && (
                      <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                        {formatAuxTimerCountdown(pumpAux.aux_timer_remain_sec ?? 0)}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end space-y-2">
              <div className="text-right">
                <p className="text-sm text-slate-600 dark:text-slate-400">Flow Rate</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                  {dashboard.actual_flow_rate}{" "}
                  <span className="text-sm font-normal text-slate-600 dark:text-slate-400">
                    LPM
                  </span>
                </p>
              </div>
              {/* Pump switch or cover icons based on Android logic */}
              {(() => {
                // When Spa is ON or Heater Cooldown is active, hide switch and show cover icon
                if (isCooldown && isSpaOn) {
                  return (
                    <div className="flex items-center gap-2" title="Heater Cooldown in Spa mode">
                      <HeaterCoverIcon className="w-8 h-8" title="Heater Cover" />
                    </div>
                  );
                }
                if (!isCooldown && isSpaOn) {
                  return (
                    <div className="flex items-center gap-2" title="Spa mode">
                      <SpaCoverIcon className="w-8 h-8" title="Spa Cover" />
                    </div>
                  );
                }
                if (isCooldown && !isSpaOn) {
                  return (
                    <div className="flex items-center gap-2" title="Heater Cooldown">
                      <HeaterCoverIcon className="w-8 h-8" title="Heater Cover" />
                    </div>
                  );
                }
                // Default: show pump switch
                return (
                  <Switch
                    checked={pumpIsOn}
                    onChange={() => {
                      const newStatus = !pumpIsOn;
                      onPumpToggle(newStatus);
                    }}
                  />
                );
              })()}
              {(pumpAux.aux_trump || hasPumpSchedule) && (
                <div className="flex items-center text-sm text-slate-600 dark:text-slate-400">
                  {pumpAux.aux_trump ? (
                    <>
                      <FiUser className="mr-1" />
                      <span>Manual Override</span>
                    </>
                  ) : (
                    <>
                      <FiClock className="mr-1" />
                      <span>Scheduled</span>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Motor Status and Prime Counter */}
          <div className="flex flex-col items-center justify-center gap-2 mt-2">
            {isVSD && pump.motor_status !== undefined && (
              <div className="flex justify-center w-full">
                {hasDryRunStatus && isDryRunPriming ? (
                  <button
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-full"
                    style={{
                      backgroundColor: Colors.BRIGHT_SUN,
                      color: Colors.NIGHT_GREY,
                    }}
                  >
                    <FiDroplet className="mr-2 w-4 h-4" />
                    Priming
                  </button>
                ) : hasDryRunStatus ? null : pump.prime_time_left > 0 && isMotorRunning(pump.motor_status as MotorStatus) ? (
                  <button
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-full"
                    style={{
                      backgroundColor: Colors.BRIGHT_SUN,
                      color: Colors.NIGHT_GREY,
                    }}
                  >
                    <FiDroplet className="mr-2 w-4 h-4" />
                    Priming
                  </button>
                ) : isCooldown && pump.pump_cooldown_left > 0 ? (
                  <button
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-full"
                    style={{
                      backgroundColor: Colors.TEAL_200,
                      color: Colors.NIGHT_GREY,
                    }}
                  >
                    <BsCloudRain className="mr-2 w-4 h-4" />
                    {`Heater Cooldown ${pump.pump_cooldown_left} Sec`}
                  </button>
                ) : highestPriorityMode ? (
                  <button
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-full"
                    style={{
                      backgroundColor: highestPriorityMode.color,
                      color: Colors.NIGHT_GREY,
                    }}
                  >
                    <FiInfo className="mr-2 w-4 h-4" />
                    {highestPriorityMode.badgeTitle}
                  </button>
                ) : isMotorRunning(pump.motor_status as MotorStatus) ? (
                  <button
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-full"
                    style={{
                      backgroundColor: Colors.ALGAE_GREEN,
                      color: Colors.NIGHT_GREY,
                    }}
                  >
                    <FiPlay className="mr-2 w-4 h-4" />
                    {
                      MOTOR_STATUS_DISPLAY_NAMES[
                        pump.motor_status as MotorStatus
                      ]
                    }
                  </button>
                ) : pump.motor_status === MotorStatus.STOPPING ? (
                  <button
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-full"
                    style={{
                      backgroundColor: Colors.BRIGHT_SUN,
                      color: Colors.NIGHT_GREY,
                    }}
                  >
                    <FiPause className="mr-2 w-4 h-4" />
                    {
                      MOTOR_STATUS_DISPLAY_NAMES[
                        pump.motor_status as MotorStatus
                      ]
                    }
                  </button>
                ) : pump.motor_status === MotorStatus.STOPPED ||
                  pump.motor_status === MotorStatus.IDLE ? (
                  <button
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-full"
                    style={{
                      backgroundColor: Colors.AVERAGE_SIGNAL,
                      color: Colors.NIGHT_GREY,
                    }}
                  >
                    <FiSquare className="mr-2 w-4 h-4" />
                    {
                      MOTOR_STATUS_DISPLAY_NAMES[
                        pump.motor_status as MotorStatus
                      ]
                    }
                  </button>
                ) : pump.motor_status === MotorStatus.ERROR ||
                  pump.motor_status === MotorStatus.OVER_TEMP ? (
                  <button
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-full"
                    style={{
                      backgroundColor: Colors.BAD_SIGNAL,
                      color: Colors.NIGHT_GREY,
                    }}
                  >
                    <FiAlertTriangle className="mr-2 w-4 h-4" />
                    {
                      MOTOR_STATUS_DISPLAY_NAMES[
                        pump.motor_status as MotorStatus
                      ]
                    }
                  </button>
                ) : (
                  <button
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-full"
                    style={{
                      backgroundColor: Colors.AVERAGE_SIGNAL,
                      color: Colors.NIGHT_GREY,
                    }}
                  >
                    <FiInfo className="mr-2 w-4 h-4" />
                    {
                      MOTOR_STATUS_DISPLAY_NAMES[
                        pump.motor_status as MotorStatus
                      ]
                    }
                  </button>
                )}
              </div>
            )}

            {/* Prime and dry run are mutually exclusive. Prefer dry run when active. */}
            {isVSD && !hasDryRunStatus && pump.prime_time_left > 0 && (
              <button className="inline-flex items-center px-3 py-1.5 text-sm font-medium bg-emerald-100 text-emerald-700 rounded-full">
                <FiDroplet className="mr-2 w-4 h-4" />
                Prime Time stop in {pump.prime_time_left}s
              </button>
            )}

            {hasDryRunStatus && dryRunInfo && !isDryRunPriming && (
              <div className="w-full max-w-md rounded-xl border border-pink-200 bg-white/80 px-4 py-3 shadow-sm dark:border-pink-900/50 dark:bg-slate-900/60">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300">
                      <FiRefreshCw className="h-4 w-4" />
                    </span>
                    <div className="text-left">
                      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        Dry Run
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {dryRunInfo.current_round} of {dryRunInfo.total_rounds} Dry Run
                      </div>
                    </div>
                  </div>
                  <span
                    className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${getDryRunStatusClassName(
                      dryRunInfo.status
                    )}`}
                  >
                    {DRY_RUN_STATUS_LABELS[dryRunInfo.status] || 'Unknown'}
                  </span>
                </div>

                {dryRunInfo.current_round > 0 && (
                  <div className="mt-3 inline-flex items-center rounded-full bg-pink-50 px-3 py-1 text-sm font-medium text-pink-700 dark:bg-pink-950/40 dark:text-pink-300">
                    Round {dryRunInfo.current_round} / {dryRunInfo.total_rounds}
                  </div>
                )}

                {dryRunInfo.protection_remaining_second > 0 && (
                  <div className="mt-4">
                    <div className="mb-2 flex items-center justify-between text-xs font-medium text-slate-600 dark:text-slate-300">
                      <span>Protection Remaining</span>
                      <span>{formatCountdown(dryRunInfo.protection_remaining_second)}</span>
                    </div>
                    <LinearProgress
                      variant="determinate"
                      value={dryRunProgress}
                      sx={{
                        height: 8,
                        borderRadius: 999,
                        backgroundColor: 'rgba(244, 114, 182, 0.12)',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 999,
                          backgroundColor: '#ec4899',
                        },
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Schedule Info */}
          {Array.isArray(scheduleInfo?.schedules) &&
            scheduleInfo.schedules.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-slate-800 dark:text-slate-200 flex items-center">
                  <FiClock className="w-4 h-4 mr-2 text-slate-600 dark:text-slate-400" />
                  Schedule
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {scheduleInfo.schedules
                    .filter(
                      (schedule) =>
                        schedule.aux_type_code === AuxTypeCode.SPALSHME_PUMP
                    )
                    .map((schedule) => (
                      <div
                        key={`${schedule.schedule_id}-${schedule.aux_type_code}`}
                        className="h-fit"
                      >
                        <SchedulePanel
                          schedule={{
                            id: schedule.schedule_id.toString(),
                            name: schedule.schedule_name,
                            time: `${formatMinutesToTime(
                              schedule.start_time
                            )} - ${formatMinutesToTime(schedule.end_time)}`,
                            days: getWeekdayArray(schedule.week_days),
                            enabled: schedule.schedule_state,
                          }}
                        />
                      </div>
                    ))}
                </div>
              </div>
            )}

          {/* Speed Control */}
          {isVSD && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-slate-800 dark:text-slate-200 flex items-center">
                  <FiActivity className="w-4 h-4 mr-2 text-slate-600 dark:text-slate-400" />
                  Speed
                </h4>
              </div>

              <div className="space-y-2">
                {isThreeSpeed ? (
                  <Slider
                    value={getThreeSpeedIndex(snapThreeSpeedSpeed((pumpAux.pump_speed ?? 0) > 0 ? pumpAux.pump_speed : THREE_SPEED_LOW))}
                    onChange={(_, newValue) => {
                      if (typeof newValue === 'number') {
                        setSpeedUI(getThreeSpeedSpeedByIndex(newValue));
                      }
                    }}
                    onChangeCommitted={(_, newValue) => {
                      if (typeof newValue === 'number') {
                        onPumpSpeedChange(getThreeSpeedSpeedByIndex(newValue));
                      }
                    }}
                    min={0}
                    max={2}
                    step={1}
                    marks={[
                      { value: 0, label: 'LOW' },
                      { value: 1, label: 'MEDIUM' },
                      { value: 2, label: 'HIGH' },
                    ]}
                    valueLabelDisplay="on"
                    valueLabelFormat={(value) => getThreeSpeedShortLabel(getThreeSpeedSpeedByIndex(Number(value)))}
                    sx={{
                      color: Colors.AQUA_MARINE,
                      height: 10,
                      '& .MuiSlider-thumb': {
                        width: 22,
                        height: 22,
                        backgroundColor: '#fff',
                        border: '2px solid currentColor',
                      },
                      '& .MuiSlider-track': {
                        border: 'none',
                      },
                      '& .MuiSlider-rail': {
                        opacity: 0.3,
                      },
                      '& .MuiSlider-markLabel': {
                        color: 'inherit',
                      },
                    }}
                  />
                ) : (
                  <>
                    <Slider
                      value={pumpAux.pump_speed}
                      onChange={(_, newValue) => {
                        if (typeof newValue === "number") {
                          setSpeedUI(Math.round(newValue));
                        }
                      }}
                      onChangeCommitted={(_, newValue) => {
                        if (typeof newValue === "number") {
                          onPumpSpeedChange(Math.round(newValue));
                        }
                      }}
                      min={40}
                      max={100}
                      step={1}
                      valueLabelDisplay="on"
                      sx={{
                        color: Colors.AQUA_MARINE,
                        height: 8,
                        "& .MuiSlider-thumb": {
                          width: 20,
                          height: 20,
                          backgroundColor: "#fff",
                          border: "2px solid currentColor",
                          "&:hover": {
                            boxShadow: "0 0 0 8px rgba(58, 133, 137, 0.16)",
                          },
                          "&.Mui-active": {
                            boxShadow: "0 0 0 12px rgba(58, 133, 137, 0.16)",
                          },
                        },
                        "& .MuiSlider-track": {
                          border: "none",
                        },
                        "& .MuiSlider-rail": {
                          opacity: 0.3,
                        },
                      }}
                    />
                    <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400 font-medium">
                      <span>40%</span>
                      <span>70%</span>
                      <span>100%</span>
                    </div>
                  </>
                )}
                {isThreeSpeed && (
                  <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
                    {getThreeSpeedManualLabel(pumpAux.pump_speed)}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Pump Linked Devices */}
      {Array.isArray(pumpLinkedDeviceList) && pumpLinkedDeviceList.length > 0 && (
        <div className="mt-6">
          <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-700 rounded-lg p-6 shadow-lg">
            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 flex items-center mb-4">
              <FiSettings className="w-5 h-5 mr-2 text-slate-600 dark:text-slate-400" />
              Pump Linked Devices
            </h3>
            <div className="space-y-4">
              {pumpLinkedDeviceList.map((device) => (
                <div
                  key={`${device.aux_type_code}-${device.aux_slot_num}`}
                  className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg border border-slate-200 dark:border-slate-700"
                >
                  <div className="flex items-center space-x-3">
                    <div
                      className={`w-3 h-3 rounded-full animate-pulse ${
                        device.aux_status ? "bg-emerald-500" : "bg-slate-400"
                      }`}
                    ></div>
                    <div>
                      <h4 className="font-medium text-slate-900 dark:text-slate-100">
                        {device.aux_tag}
                      </h4>
                      <div className="flex items-center text-sm text-slate-600 dark:text-slate-400 mt-1">
                        {(() => {
                          const hasSchedule = Boolean(
                            (scheduleInfo?.schedules || []).some(
                              (s) =>
                                s.aux_type_code === device.aux_type_code &&
                                ((s.aux_slot ?? s.slot_num) === device.aux_slot_num)
                            )
                          );
                          if (!device.aux_trump && !hasSchedule) return null;
                          return device.aux_trump ? (
                            <span className="inline-flex items-center px-2 py-0.5 text-xs border border-slate-200 rounded mr-2">
                              <FiUser className="mr-1" />
                              Manual
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 text-xs border border-slate-200 rounded mr-2">
                              <FiClock className="mr-1" />
                              Scheduled
                            </span>
                          );
                        })()}
                      </div>
                      {(device.aux_timer ?? 0) > 0 && (
                        <div className="mt-2 flex flex-col gap-1">
                          <span className="inline-flex w-fit items-center rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 dark:border-slate-700 dark:text-slate-300">
                            <FiClock className="mr-1.5 h-3.5 w-3.5" />
                            Timer {formatAuxTimerDuration(device.aux_timer ?? 0)}
                          </span>
                          {!!device.aux_status && (device.aux_timer_remain_sec ?? 0) > 0 && (
                            <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                              {formatAuxTimerCountdown(device.aux_timer_remain_sec ?? 0)}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  {(
                    device.aux_type_code === AuxTypeCode.POOL_PRESSURE_CLEANER ||
                    device.aux_type_code === AuxTypeCode.POOL_IN_FLOOR_CLEANING
                  ) && isSpaOn ? (
                    <div
                      className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-300 cursor-not-allowed"
                      title="Spa 正在运行，Cleaner 开关不可用"
                    >
                      <FiSlash className="w-5 h-5" />
                    </div>
                  ) : isCooldown ? (
                    <div
                      className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-300 cursor-not-allowed"
                      title="Heater Cooldown，开关不可用"
                    >
                      <FiSlash className="w-5 h-5" />
                    </div>
                  ) : (
                    <Switch
                      checked={device.aux_status}
                      onChange={() => {
                        onPumpLinkedDeviceToggle(
                          device.aux_type_code,
                          device.aux_slot_num,
                          !device.aux_status
                        );
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainPumpSection;
